/*
 * Copyright (c) 2008-2025 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */
import MeetingTypesMixin from "@/components/MeetingTypesMixin"
import MeetingTemplatesMixin from "@/components/MeetingTemplatesMixin"
import UserProperty from "@/components/cockpit/UserProperty"
import { COMPANY_ONLINE_SHOP } from "../config/company"
import Vue from "vue"
import VueCookie from "vue-cookie"
import VueI18n from "vue-i18n"

Vue.use(VueI18n)
Vue.use(VueCookie)

/**
 * Main app I18N messagees
 */
const messages = {
  en: {
    start: "Start",
    end: "End",
    location: "Location",
    details: "Details",
    export: "Export",
    text: "Text",
    or: "or",
    points: "Points",
    encoding: "Encoding",
    name: "Name",
    low: "Low",
    high: "High",
    configure: "Configure",
    length: "Length",
    specialChars: {
      semicolon: "Semicolon",
      comma: "Comma",
    },
    language: "Language",
    languages: {
      english: "English",
      german: "German",
    },
    lowerCase: "Lowercase",
    upperCase: "Uppercase",
    digits: "Digits",
    date: {
      timezone: "Timezone",
    },
    extended: "(Extended)",
    meetings: {
      title: "Title",
      subTitle: "Description",
      location: "Location",
      startDate: "Start",
      endDate: "End",
      timezone: "Timezone",
      autojoin: "Autojoin",
      participantPassword: "Participant Password",
      facilitatorPassword: "Facilitator Password",
      actions: "Actions",
      upload: "Meeting Upload",
    },
    uploadInProgress: "Uploading and processing meeting file...",
    uploadError: {
      400: "Please select a single meeting file.",
      402: "The file could not be processed due to an exceeded license. Please upgrade your license",
      422: "The file could not be processed. Possibly it is not a valid meeting file.",
      generic: "The file could not be processed.",
    },
    candidate: "Candidate",
    list: "List",
    interactions: {
      vote: "Single/Multiple Choice",
      starvote: "Star Voting",
      imagevote: "Image Voting",
      "person-election": "Person Election",
      "list-election": "List Election",
      quiz: "Quiz Results",
      opinionpoll: "Opinion Poll",
      category: "Category Selection",
      numeric: "Numerical scales",
      hotornot: "Hot or Not",
      slide: "Info Page",
      presentation: "Presentation",
      "hidden-message": "Production Note",
      prioritize: "Prioritize",
      text: "Free text & Cluster",
      wordcloud: "Word cloud",
      qa: "Q&A",
      message: "Message",
      text_cluster: "Collect & Cluster",
      text_prioritize: "Collect & Prioritize",
      text_cluster_prioritize: "Collect & Cluster & Prioritize",
      welcome: "Welcome",
      messagericheditor: "Rich Text Message",
      xymatrix: "XY Matrix",
      correlation: "Correlation Analysis",
      "tag-correlation": "Voter Group Breakdown",
    },
    new: {
      vote: "How important do you find this topic?",
      numeric: "Rate the following statements!",
      imagevote: "Which icon best reflects your current mood?",
      text: "Please enter your contributions!",
      qa: "Share with us your questions and thoughts on the topic!",
      wordcloud: "Which topics should we work on?",
      message: "<h2>Welcome!</h2><p>Here interactions are displayed as soon as they are activated.</p>",
      xymatrix: "Evaluate a topic based on two characteristics",
      quiz: "Evaluation of quiz results",
      election: "Zur Wahl der/des _ ist vorgeschlagen", // German text to align with server-side toolset language (DEV-1996)
      correlation: "Correlation Analysis",
      "tag-correlation": "Analysis by Voter Group",
    },
    vote: {
      option: {
        1: "unimportant",
        2: "rather unimportant",
        3: "rather important",
        4: "important",
      },
    },
    numeric: {
      option: {
        1: "Characteristic 1",
        2: "Characteristic 2",
        3: "Characteristic 3",
      },
      rangeOptions: {
        0: "Custom",
        1: "0 - 10 (single step)",
        2: "1 - 10 (single step)",
        3: "1 - 6 (single step)",
        4: "1 - 100 (single step)",
      },
      rangeOptionTexts: {
        min: "Min. Value",
        max: "Max. Value",
        increment: "Step Count",
      },
    },
    phaseNames: {
      display: "Display",
      explanation: "Introduce",
      action: "Activate",
      result: "Evaluate",
      solve: "Solve",
      nomination: "Nomination",
      addenda: "Addenda",
      introduction: "Introduction",
      ballot: "Ballot",
      results: "Result",
      verification: "Election commission",
      acceptance: "Acceptance",
    },
    surveyPhaseNames: {
      result: "Present results",
    },
    configureInteraction: "Configure {type}",
    resultDisplayStyles: {
      BAR_2D: "Bar chart",
      COLUMN_2D: "Column chart",
      PIE_2D: "Pie chart",
      SLIDER: "Slider diagram",
      HEATMAP: "Heat map",
      BADGE: "List",
      TEXT_TOOL_CLOUD_RESULT: "Word Cloud (Contributions)",
      TEXT_TOOL_WORD_CLOUD: "Word Cloud (Words)",
      TEXT_TOOL_RESULT: "Contribution List",
      TEXT_TOOL_BLOG_RESULT: "Contribution Cards",
      TEXT_TOOL_CLUSTER: "Cluster List",
      TEXT_TOOL_CLUSTER_RESULT: "Cluster",
      TEXT_TOOL_CATEGORY_CONTRIBUTION: "Contributions",
      TEXT_TOOL_CATEGORY_CLUSTER: "Cluster",
      TEXT_TOOL_CATEGORY_BOTH: "Cluster & Contributions",
      star: {
        BAR_2D: "Bar chart",
        COLUMN_2D: "Column chart",
        PIE_2D: "Pie chart",
        BADGE: "Stars",
      },
      xy: {
        MATRIX_AVERAGE: "Average",
        MATRIX_INDIVIDUAL: "Individual Contributions",
        MATRIX_AVERAGE_INDIVIDUAL: "Average & Contributions",
      },
    },
    addons: {
      facilitatorView: "Facilitator's View",
      clusterView: "Cluster View",
      electionCommissionView: "Election Commission",
    },
    htmlAllowed: "HTML allowed",
    headline: "Headline",
    question: "Question",
    countdown: "Countdown",
    seconds: "seconds",
    resultDisplay: "Presentation View",
    tenantManagement: "Tenant Management",
    userManagement: "User Accounts",
    licenseManagement: "License Packages",
    lanEvent: "LAN Event",
    maintenanceMessage: "Maintenance Message",
    navigation: {
      info: "Configuration",
      participants: "Participants",
      script: "Interactions",
      results: "Results",
    },
    actions: {
      edit: "Edit",
      delete: "Delete",
    },
    forms: {
      new: "New",
      edit: "Edit",
      reset: "Reset",
      save: "Save",
      cancel: "Cancel",
    },
    confirms: {
      hideMeeting: "Hide meeting '{title}' ?",
      cancel: "Cancel",
      close: "Close",
      save: "Save",
      upload: "Upload",
      ok: "OK",
      upgrade: "Upgrade",
      inquire: "Inquire",
    },
    buttons: {
      enable: "Enable",
      disable: "Disable",
      reset: "Reset",
    },
    contact: {
      phone: "Phone",
      email: "Email",
      website: "Website",
    },
    userPropertyType: {
      string: "Text",
      number: "Number",
      boolean: "Yes/No",
    },
    interactionConfig: {
      errorMessage: "Error: This input field only accepts integer number.",
      restrictContributions: "Reject contributions when result is displayed.",
      restrictContributionsToolTip:
        "Activate this switch if latecomers should no longer change the result due to network runtimes.<br>This setting\
        is useful in <b>formal elections</b> if no more ballot papers may be accepted when the election result is\
        displayed.",
      anonymous: "Anonymous voting",
      anonymousToolTip:
        "When activated, votes are anonymous. Switching is only allowed while no contributions exist yet and the interaction is not active.",
    },
    roles: {
      meetingAdministration: {
        name: "Meeting Administration",
        description:
          "Users with this role can see, edit and delete all meetings in the system. Not just their own ones.",
      },
      staffUserAdministration: {
        name: "User Administration",
        description:
          "Users with this role can create new users, see all users in the system, edit their data, reset their passwords and delete them.",
      },
      cockpit: {
        name: "Cockpit Operator",
        description:
          "Users with this role can download and run the Meeting Cockpit (a Java application providing advanced control capabilities).",
      },
      Customer: {
        name: "User",
        description: "Users with this role can create, edit and delete own meetings.",
      },
      agenda: {
        name: "Agenda",
        description:
          "Users with this role can upload data for an agenda page within the main menu. (Requires specific server-side configuration)",
      },
      speakerInfos: {
        name: "Speaker Infos",
        description:
          "Users with this role can upload information and images for a speaker infos page within the main menu. (Requires specific server-side configuration)",
      },
      travelInfos: {
        name: "Travel Infos",
        description:
          "Users with this role can upload information for a travel info page within the main menu. (Requires specific server-side configuration)",
      },
      lockScreenImages: {
        name: "Lockscreen-Images",
        description:
          "Users with this role can upload images to be displayed on the lock screen. (Requires specific server-side configuration)",
      },
      manual: {
        name: "Manual",
        description: "Users with this role can access the manual at help/index.html.",
      },
      tenantAdministration: {
        name: "Tenant Administration",
        description: "Users with this role can see, edit and delete all tenants in the system.",
      },
    },
    integration: {
      None: "None",
      Zoom: "Zoom (Videoconference)",
      Livestream: "Live stream",
      Iframe: "Iframe",
      Nanocosmos: "nanoStream (Livestream)",
    },
    inline: {
      left: "Inline Left",
      center: "Inline Center",
      right: "Inline Right",
    },
    position: {
      inline: "Inline",
      top: "Top",
      left: "Left",
      bottom: "Bottom",
      right: "Right",
      fullscreen: "Fullscreen",
    },
    size: {
      original: "Original",
      small: "Small",
      medium: "Medium",
      large: "Large",
      fullwidth: "Full width",
    },
    license: {
      noMeetingRoomOfTypeLicensed:
        "<b>Attention:</b> With the current license package you are not eligible to start a meeting of type '{type}'",
      noMeetingRoomOfTypesLicensed:
        "<b>Attention:</b> With the current license package you are not eligible to start meetings of the following types '{types}'",
      // todo: link should point to a page with information about licensing details, meeting rooms etc.
      linkToAdditionalInformation:
        "<a href='https://" + COMPANY_ONLINE_SHOP + "' target='_blank'>More information...</a>",
      meetingRoomCapacityInfo:
        "The <b>capacity limit</b> (max. number of online participants) granted by your current license.",
      meetingRoomCapacityExhaustionWarnText:
        "You are approaching the <b>capacity limit</b> (max. number of online participants) licensed for your event. Once you hit the" +
        " limit, further <b>participants will be rejected</b>.",
      meetingRoomCapacityExhaustionErrorText:
        "You've hit the <b>capacity limit</b> (max. number of online participants) licensed for your event. Further <b>participants will" +
        " now be rejected</b>.",
    },
    status: {
      status: "Status",
      capacity: "Capacity",
      total: "Total",
      favorite: "Favorites",
      OFFLINE: "Offline",
      IN_WAITING_ROOM: "In waiting room",
      IN_A_MEETING: "In meeting",
      IN_CURRENT_MEETING: "In meeting",
      IN_OTHER_MEETING: "In other meeting",
    },
    signOutAll: {
      action: "Sign out all participants",
      confirmTitle: "Sign out all participants",
      confirmText:
        "Are you sure you want to logout all participants from this meeting?<br>If you wish to continue the meeting, the participants will have to log in again.",
    },
    meetingRoomConfiguration: "Meeting execution",
    waitingRoomConfiguration: "Waiting room configuration",
    accreditationConfiguration: "Accreditation configuration",
    meetingDataAutoDeletion: {
      autoDeleteInDays: "{data} will be <b>deleted {days}</b> (on <b>{date}</b>)",
      autoDeleteInDaysShort: "Data will be <b>deleted {days}</b> (on <b>{date}</b>)",
      remainingDays: "today | tomorrow | in {count} days",
      startStopMeetingToExtendRetentionPeriodHint:
        "To restart the data deletion countdown, start and stop the meeting.",
      autoDeleteAfterMeetingStopped: "{data} will be deleted {days} after the meeting is stopped",
      autoDeleteAfterMeetingStoppedShort: "Data will be deleted {days} after the meeting is stopped.",
      daysAfterMeetingStopped: "the day | {count} days",
      autoDeletionData: {
        Participants: "Participants data",
        Contributions: "Contributions",
        ParticipantsAndContributions: "Participants data and contributions",
        EntireMeeting: "The entire meeting",
      },
    },
    licenseFeature: {
      QUIZ: "Quiz",
      ELECTION: "Election",
      MEETING: "Meeting",
      MultiUser: "Permit configuration of multiple users",
      MeetingResults: "Meeting results",
      JmcUsage: "Usage of meeting cockpit",
      MeetingRoomCapacity: "Size of meeting rooms",
      MeetingRoomCount: "No. of meeting rooms - total",
      WaitingRoom: "Permit usage of waiting rooms",
      RoleBasedMeetingSharing: "Permit role-base meeting sharing",
      CustomMeetingProperties: "Custom meeting properties",
      TimeTracking: "Participants' online time tracking",
      ProxyVoting: "Proxy votings",
      OIDCSSO: "Login with SSO (OAuth 2/OpenId Connect)",
      SimpleFeedbackView: "SimpleFeedbackView",
      Accreditation: "Accreditation",
    },
    serverStatistics: "Server Statistics",
    ...MeetingTypesMixin.i18n.messages.en,
    ...MeetingTemplatesMixin.i18n.messages.en,
    ...UserProperty.i18n.messages.en,
  },
  de: {
    start: "Start",
    end: "Ende",
    location: "Ort",
    details: "Details",
    export: "Export",
    text: "Text",
    or: "oder",
    points: "Punkte",
    encoding: "Codierung",
    name: "Bezeichnung",
    low: "Niedrig",
    high: "Hoch",
    configure: "Konfigurieren",
    length: "Länge",
    specialChars: {
      semicolon: "Semikolon",
      comma: "Komma",
    },
    language: "Sprache",
    languages: {
      english: "Englisch",
      german: "Deutsch",
    },
    lowerCase: "Kleinbuchstaben",
    upperCase: "Großbuchstaben",
    digits: "Ziffern",
    date: {
      timezone: "Zeitzone",
    },
    meetings: {
      title: "Titel",
      subTitle: "Beschreibung",
      location: "Ort",
      startDate: "Start",
      endDate: "Ende",
      timezone: "Zeitzone",
      autojoin: "Autojoin",
      participantPassword: "Passwort Teilnehmer",
      facilitatorPassword: "Passwort Moderator",
      actions: "Aktionen",
      upload: "Meeting Hochladen",
    },
    uploadInProgress: "Meeting-Datei wird hochgeladen und verarbeitet...",
    uploadError: {
      400: "Bitte wählen Sie eine einzelne Meeting-Datei aus.",
      402: "Die Datei konnte nicht verarbeitet werden. Mit der aktuellen Lizenz können Sie Meetings dieses Typs nicht starten.",
      422: "Die Datei konnte nicht verarbeitet werden. Möglicherweise handelt es sich nicht um eine valide Meeting-Datei.",
      generic: "Die Datei konnte nicht verarbeitet werden.",
    },
    candidate: "Kandidat/in",
    list: "Liste",
    interactions: {
      vote: "Single/Multiple Choice",
      starvote: "Sternchen-Bewertung",
      imagevote: "Abstimmung mit Bildern",
      "person-election": "Personenwahl",
      "list-election": "Listenwahl",
      quiz: "Quiz-Ergebnis",
      opinionpoll: "Stimmungs-Barometer",
      category: "Kategorie-Auswahl",
      numeric: "Numerische Skalenabfrage",
      hotornot: "Hot or Not",
      slide: "Info-Seite",
      presentation: "Präsentation",
      "hidden-message": "Regie-Hinweis",
      prioritize: "Priorisieren",
      text: "Freitext-Frage & Clustern",
      wordcloud: "Word-Cloud",
      qa: "Moderierte Fragen (Q&A)",
      message: "Mitteilung",
      text_cluster: "Sammeln & Clustern",
      text_prioritize: "Sammeln & Priorisieren",
      text_cluster_prioritize: "Sammeln & Clustern & Priorisieren",
      welcome: "Begrüßung",
      messagericheditor: "Rich-Text-Mitteilung",
      xymatrix: "XY-Matrix",
      correlation: "Korrelations-Auswertung",
      "tag-correlation": "Auswertung nach Wählergruppe",
    },
    new: {
      vote: "Wie wichtig finden Sie dieses Thema?",
      numeric: "Bewerten Sie die folgenden Aussagen!",
      imagevote: "Welches Icon gibt Ihre momentane Stimmung am besten wieder?",
      text: "Bitte geben Sie Ihre Beiträge ein!",
      qa: "Teilen Sie mit uns Ihre Fragen und Gedanken zum Thema!",
      wordcloud: "Welche Themen sollen wir bearbeiten?",
      message:
        "<h2>Herzlich willkommen!</h2><p>Hier werden Interaktionen angezeigt, sobald sie freigeschaltet werden.</p>",
      xymatrix: "Bewerten Sie ein Thema anhand von zwei Merkmalen",
      quiz: "Auswertung der Quiz-Ergebnisse",
      election: "Zur Wahl der/des _ ist vorgeschlagen",
      correlation: "Korrelations-Auswertung",
      "tag-correlation": "Auswertung der Abstimmung nach Wählergruppe",
    },
    vote: {
      option: {
        1: "unwichtig",
        2: "eher unwichtig",
        3: "eher wichtig",
        4: "wichtig",
      },
    },
    numeric: {
      option: {
        1: "Eigenschaft 1",
        2: "Eigenschaft 2",
        3: "Eigenschaft 3",
      },
      rangeOptions: {
        0: "Benutzerdefiniert",
        1: "0 - 10 (Einerschritte)",
        2: "1 - 10 (Einerschritte)",
        3: "1 - 6 (Einerschritte)",
        4: "1 - 100 (Einerschritte)",
      },
      rangeOptionTexts: {
        min: "Min. Wert",
        max: "Max. Wert",
        increment: "Schrittweite",
      },
    },
    phaseNames: {
      display: "Anzeigen",
      explanation: "Anmoderieren",
      action: "Freigeben",
      result: "Auswerten",
      solve: "Auflösen",
      nomination: "Nominierung",
      addenda: "Ergänzungen",
      introduction: "Vorstellung",
      ballot: "Wahl",
      results: "Ergebnis",
      verification: "Wahlkommission",
      acceptance: "Annahme",
    },
    surveyPhaseNames: {
      result: "Ergebnisse präsentieren",
    },
    configureInteraction: "{type} konfigurieren",
    resultDisplayStyles: {
      BAR_2D: "Balken-Diagramm",
      COLUMN_2D: "Säulen-Diagramm",
      PIE_2D: "Torten-Diagramm",
      SLIDER: "Slider-Diagramm",
      HEATMAP: "Heat-Map",
      BADGE: "Liste",
      TEXT_TOOL_CLOUD_RESULT: "Word Cloud (Beiträge)",
      TEXT_TOOL_WORD_CLOUD: "Word Cloud (Wörter)",
      TEXT_TOOL_RESULT: "Beitrags-Liste",
      TEXT_TOOL_BLOG_RESULT: "Beitrags-Karten",
      TEXT_TOOL_CLUSTER: "Cluster-Liste",
      TEXT_TOOL_CLUSTER_RESULT: "Cluster",
      TEXT_TOOL_CATEGORY_CONTRIBUTION: "Beiträge",
      TEXT_TOOL_CATEGORY_CLUSTER: "Cluster",
      TEXT_TOOL_CATEGORY_BOTH: "Cluster & Beiträge",
      star: {
        BAR_2D: "Balken-Diagramm",
        COLUMN_2D: "Säulen-Diagramm",
        PIE_2D: "Torten-Diagramm",
        BADGE: "Sternchen",
      },
      xy: {
        MATRIX_AVERAGE: "Mittelwert",
        MATRIX_INDIVIDUAL: "Individuelle Beiträge",
        MATRIX_AVERAGE_INDIVIDUAL: "Mittelwert & Beiträge",
      },
    },
    addons: {
      facilitatorView: "Moderations-Ansicht",
      clusterView: "Clustern",
      electionCommissionView: "Wahlkommission",
    },
    htmlAllowed: "HTML erlaubt",
    headline: "Überschrift",
    question: "Fragestellung",
    countdown: "Countdown",
    seconds: "Sekunden",
    resultDisplay: "Präsentationsansicht",
    tenantManagement: "Mandanten-Verwaltung",
    userManagement: "Benutzer-Verwaltung",
    licenseManagement: "Lizenzpakete",
    lanEvent: "LAN Event",
    maintenanceMessage: "Wartungshinweis",
    navigation: {
      info: "Konfiguration",
      participants: "Teilnehmer",
      script: "Interaktionen",
      results: "Ergebnisse",
    },
    actions: {
      edit: "Bearbeiten",
      delete: "Löschen",
    },
    forms: {
      new: "Neu",
      edit: "Bearbeiten",
      reset: "Reset",
      save: "Speichern",
      cancel: "Abbrechen",
    },
    confirms: {
      hideMeeting: "Meeting '{title}' ausblenden?",
      cancel: "Abbrechen",
      close: "Schließen",
      save: "Speichern",
      upload: "Hochladen",
      ok: "OK",
      upgrade: "Upgrade",
      inquire: "Anfragen",
    },
    buttons: {
      enable: "Aktivieren",
      disable: "Deaktivieren",
      reset: "Zurücksetzen",
    },
    contact: {
      phone: "Telefon",
      email: "Email",
      website: "Website",
    },
    userPropertyType: {
      string: "Text",
      number: "Zahl",
      boolean: "Ja/Nein",
    },
    extended: "(Erweitert)",
    interactionConfig: {
      errorMessage: "Fehler: Dieses Eingabefeld akzeptiert nur Ganzzahlen.",
      restrictContributions: "Beiträge ablehnen, sobald das Ergebnis angezeigt wird.",
      restrictContributionsToolTip:
        'Aktivieren Sie diesen Schalter, wenn "Nachzügler" aufgrund von Netzlaufzeiten das Ergebnis nicht mehr ändern\
        sollen.<br>Diese Einstellung ist insbesondere bei <b>formalen Wahlen</b> sinnvoll, wenn mit der Anzeige des\
        Wahlergebnisses keine Stimmzettel mehr akzeptiert werden dürfen.',
      anonymous: "Geheim abstimmen",
      anonymousToolTip:
        "Wenn aktiviert, ist die Abstimmung geheim. Umschalten ist nur so lange erlaubt, wie noch keine Beiträge existieren und die Interaktion nicht aktiv ist.",
    },
    roles: {
      meetingAdministration: {
        name: "Meeting-Verwaltung",
        description: "Benutzer mit dieser Rolle können alle Meetings im System sehen, bearbeiten und löschen.",
      },
      staffUserAdministration: {
        name: "Benutzer-Verwaltung",
        description:
          "Benutzer mit dieser Rolle können neue Nutzer erstellen, vorhandene Nutzer sehen, bearbeiten und löschen, sowie deren Passwörter zurücksetzen.",
      },
      cockpit: {
        name: "Cockpit Operator",
        description:
          "Benutzer mit dieser Rolle können das Meeting Cockpit (Java Anwendung für erweiterte Steuerung) herunterladen und ausführen.",
      },
      Customer: {
        name: "Benutzer",
        description: "Benutzer mit dieser Rolle können eigene Meetings erstellen, bearbeiten und löschen.",
      },
      agenda: {
        name: "Agenda",
        description:
          "Benutzer mit dieser Rolle können Daten für eine Agenda-Seite im Menü hochladen. (Erfordert spezielle serverseitige Konfiguration)",
      },
      speakerInfos: {
        name: "Rednerliste",
        description:
          "Benutzer mit dieser Rolle können Informationen und Bilder für eine Seite mit Rednerinfos im Menü hochladen. (Erfordert spezielle serverseitige Konfiguration)",
      },
      travelInfos: {
        name: "Reiseinfos",
        description:
          "Benutzer mit dieser Rolle können Informationen für eine Seite mit Reiseinfos im Menü hochladen. (Erfordert spezielle serverseitige Konfiguration)",
      },
      lockScreenImages: {
        name: "Sperrbildschirm-Bilder",
        description:
          "Benutzer mit dieser Rolle können Bilder für die Anzeige auf dem Sperrbildschirm hochladen. (Erfordert spezielle serverseitige Konfiguration)",
      },
      manual: {
        name: "Hilfe",
        description: "Benutzer mit dieser Rolle können die Hilfe unter help/index.html einsehen.",
      },
      tenantAdministration: {
        name: "Mandanten-Verwaltung",
        description: "Benutzer mit dieser Rolle können alle Mandanten im System sehen, bearbeiten und löschen.",
      },
    },
    integration: {
      None: "Keine",
      Zoom: "Zoom (Videokonferenz)",
      Livestream: "Livestream",
      Iframe: "Iframe",
      Nanocosmos: "nanoStream (Livestream)",
    },
    inline: {
      left: "Linksbündig",
      center: "Zentriert",
      right: "Rechtsbündig",
    },
    position: {
      inline: "Eingebettet",
      top: "Oben",
      left: "Links",
      bottom: "Unten",
      right: "Rechts",
      fullscreen: "Vollbild",
    },
    size: {
      original: "Originalgröße",
      small: "Klein",
      medium: "Mittel",
      large: "Groß",
      fullwidth: "Volle Breite",
    },
    license: {
      noMeetingRoomOfTypeLicensed:
        "<b>Achtung:</b> Mit der aktuellen Lizenz können Sie Meetings vom Typ {type} nicht starten.",
      noMeetingRoomOfTypesLicensed:
        "<b>Achtung:</b> Mit der aktuellen Lizenz können Sie Meetings mit den Typen <b>{types}</b> nicht starten.",
      // todo: link should point to a page with information about licensing details, meeting rooms etc.
      linkToAdditionalInformation:
        "<a href='https://" + COMPANY_ONLINE_SHOP + "' target='_blank'>Weitere Informationen...</a>",
      meetingRoomCapacityInfo:
        "Die <b>Kapazitätsgrenze</b> (maximale Anzahl gleichzeitig verbundener Teilnehmer) Ihrer aktuellen Lizenz.",
      meetingRoomCapacityExhaustionWarnText:
        "Sie nähern sich der lizenzierten <b>Kapazitätsgrenze</b> (maximale Anzahl gleichzeitig verbundener Teilnehmer) " +
        "für dieses Event. Sobald Sie die Grenze erreicht haben, können sich <b>keine weiteren Teilnehmer mehr anmelden</b>.",
      meetingRoomCapacityExhaustionErrorText:
        "Die für Ihre Veranstaltung lizenzierte <b>Kapazitätsgrenze</b> (maximale Anzahl gleichzeitig verbundener Teilnehmer) " +
        "wurde erreicht. Weitere <b>Teilnehmer können sich nicht mehr anmelden</b>.",
    },
    status: {
      status: "Status",
      capacity: "Kapazität",
      total: "Gesamt",
      favorite: "Favoriten",
      OFFLINE: "Offline",
      IN_WAITING_ROOM: "Im Warteraum",
      IN_A_MEETING: "Im Meeting",
      IN_CURRENT_MEETING: "Im Meeting",
      IN_OTHER_MEETING: "In anderem Meeting",
    },
    signOutAll: {
      action: "Alle Teilnehmer abmelden",
      confirmTitle: "Alle Teilnehmer abmelden",
      confirmText:
        "Sind Sie sicher, dass Sie sämtliche Teilnehmer von diesem Meeting abmelden möchten?<br>Sollten Sie das Meeting fortsetzen wollen, müssen sich die Teilnehmer erneut anmelden.",
    },
    meetingRoomConfiguration: "Ablauf des Meetings",
    waitingRoomConfiguration: "Konfiguration des Warteraums",
    accreditationConfiguration: "Akkreditierung konfigurieren",
    meetingDataAutoDeletion: {
      autoDeleteInDays: "{data} <b>{days} gelöscht</b> (am <b>{date}</b>)",
      autoDeleteInDaysShort: "Daten werden <b>{days} gelöscht</b> (am <b>{date}</b>)",
      remainingDays: "heute | morgen | in {count} Tagen",
      startStopMeetingToExtendRetentionPeriodHint:
        "Um den Countdown für die Datenlöschung erneut zu starten, starten und beenden Sie das Meeting.",
      autoDeleteAfterMeetingStopped: "{data} {days} nach Beenden des Meetings gelöscht",
      autoDeleteAfterMeetingStoppedShort: "Daten werden {days} nach Beenden des Meetings gelöscht.",
      daysAfterMeetingStopped: "am Tag | {count} Tage",
      autoDeletionData: {
        Participants: "Teilnehmer-Daten werden",
        Contributions: "Beiträge werden",
        ParticipantsAndContributions: "Teilnehmer-Daten und Beiträge werden",
        EntireMeeting: "Das gesamte Meeting wird",
      },
    },
    licenseFeature: {
      QUIZ: "Quiz",
      ELECTION: "Election",
      MEETING: "Meeting",
      MultiUser: "Konfiguration von mehreren Benutzern zulassen",
      MeetingResults: "Meeting-Ergebnisse",
      JmcUsage: "Anwendung des Meeting Cockpit",
      MeetingRoomCapacity: "Größe der Meetingräume",
      MeetingRoomCount: "Nr. der Meetingräume - gesamt",
      WaitingRoom: "Benutzung von Warteräumen zulassen",
      RoleBasedMeetingSharing: "Rollenbasiertes Teilen von Meetings zulassen",
      CustomMeetingProperties: "Benutzerdefinierte Meeting-Eigenschaften",
      TimeTracking: "Anwesenheitserfassung der Teilnehmer",
      ProxyVoting: "Stimmrechtsvertretungen",
      OIDCSSO: "Anmeldung mit SSO (OAuth 2/OpenId Connect)",
      SimpleFeedbackView: "SimpleFeedbackView",
      Accreditation: "Akkreditierung",
    },
    serverStatistics: "Server Statistics",
    ...MeetingTypesMixin.i18n.messages.de,
    ...MeetingTemplatesMixin.i18n.messages.de,
    ...UserProperty.i18n.messages.de,
  },
}

/**
 * Setup of I18N date formats
 */
const dateTimeFormats = {
  en: {
    short: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    long: {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
    time: {
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    },
  },
  de: {
    short: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    long: {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
    time: {
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    },
  },
}

// Determine user/browser language to use as default in i18n setting
var userLang = navigator.language || navigator.userLanguage
userLang = userLang.substring(0, 2) === "de" ? "de" : "en"

export default new VueI18n({
  // Restore locale from cookie, if it was set
  locale: VueCookie.get("locale") || userLang,
  dateTimeFormats,
  messages,
  silentTranslationWarn: true,
})
